import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function AuthPage({ page, csrfToken }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo') || '/';
    const isSignup = (page === 'signup');
    const isLogin = (page === 'login');

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const url = isSignup ? '/auth/signup' : '/auth/login';
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-csrf-token': csrfToken,
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Login failed');
                setLoading(false);
                return;
            }

            window.location.href = data?.redirect || returnTo;
        } catch {
            setError('Something went wrong. Please try again.');
            setLoading(false);
        }
    }

    return (
        <div className='flex-1 h-full bg-[#111] lg:bg-[#070707] overflow-auto custom-scroll'>
            <div className='mt-12 mx-auto 2xl:w-[1440px] lg:h-[900px] flex flex-col lg:flex-row'>
                <div className='hidden lg:flex flex-1 bg-[url(/static/images/auth-bg.png)] flex-col justify-center'>
                    <div className='ml-[60px] w-[380px]'>
                        <div className='flex items-center h-6'>
                            <div className='flex-center px-3 py-1 gap-1 rounded-full bg-green1/20 opacity-80 text-xs11 font-medium text-green1'>
                                <img src='/static/images/icons/zap.svg' className='w-3 h-3 svg-green1' />
                                COMPRESSION
                            </div>
                            <div className='flex-center px-3 py-1 gap-1 rounded-full bg-[#0c0c0d]/40 border border-white/20 opacity-80 text-xs11 font-medium text-white1/70'>
                                AI FRAMING
                            </div>
                            <div className='flex-center px-3 py-1 gap-1 rounded-full bg-[#0c0c0d]/40 border border-white/20 opacity-80 text-xs11 font-medium text-white1/70'>
                                CLIENT DELIVERY
                            </div>
                        </div>

                        <div className='mt-6 w-[280px] h-[177px] text-[54px] text-[#eef0f5] font-semibold leading-[104%] tracking-[-1.5px]'>
                            Compress.<br />
                            Reframe.<br />
                            <span className='text-[#13d67a]'>Deliver.</span>
                        </div>

                        <div className='mt-4 w-[360px] text-[#eef0f5]/60 text-sm leading-[175%]'>
                            Patent-pending compression cuts file size in half with
                            <span className='text-[#c2c8d8] font-semibold'> zero quality loss. </span>
                            AI reframing converts 16:9 to 9:16 in one click.
                            Instant delivery links — everything your clients need, nothing they don't.
                        </div>

                        <div className='mt-10 w-[380px] h-[69px] flex rounded-[14px] bg-[#0b0c0f]/55 border border-white/10 backdrop-filter-[blur(8px)]'>
                            <div className='flex flex-col justify-center items-start gap-1 px-5 border-r border-white/7'>
                                <div className='text-[#5e6378] text-[10px] tracking-[1px]'>AVG SIZE SAVED</div>
                                <div className='text-[#13d67a]'>-52%</div>
                            </div>
                            <div className='flex flex-col justify-center items-start gap-1 px-5'>
                                <div className='text-[#5e6378] text-[10px] tracking-[1px]'>AI REFRAME</div>
                                <div className='text-[#eef0f5]'>16:9 → 9:16</div>
                            </div>
                            <div className='flex flex-col justify-center items-start gap-1 px-5 border-l border-white/7'>
                                <div className='text-[#5e6378] text-[10px] tracking-[1px]'>CREATORS</div>
                                <div className='text-[#eef0f5]'>47k+</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='flex-1 bg-[#111] flex-center p-10'>
                    <div className='w-full max-w-[360px]'>
                        <a href='/' title='JPEGmini Cloud' className='flex-center gap-2'>
                            <div className='flex-center w-8 h-8 rounded-lg bg-green1 text-[#111] text-[15px] font-extrabold'>Jm</div>
                            <div className='flex items-end gap-1'>
                                <div className='font-bold text-white1 leading-[1]'>JPEGmini</div>
                                <div className='text-white1/70 text-[10px] font-semibold tracking-[0.6px] leading-[1]'>CLOUD</div>
                            </div>
                        </a>

                        <div className='mt-8 text-center text-white text-[28px] font-semibold leading-[1.2]'>
                            {isSignup ? <div>Start delivering<br />in minutes.</div> : 'Welcome back.'}
                        </div>

                        <div className='mt-4 text-center text-white/55 text-sm leading-[140%]'>
                            {isSignup ? 'Create your free account. No credit card needed.' : 'Log in to your JPEGmini Cloud account.'}
                        </div>

                        {(error || searchParams.get('error')) && (
                            <div className='mt-8 p-3 rounded-lg bg-red-900/30 border border-red-800 text-red-300 text-sm'>
                                {error || 'Authentication failed. Please try again.'}
                            </div>
                        )}

                        <form className='mt-8' onSubmit={handleSubmit}>
                            <input
                                type='email'
                                placeholder='Email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className='auth-input'
                                required
                                autoFocus
                            />

                            <input
                                type='password'
                                placeholder='Password'
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className='auth-input mt-4'
                                required
                            />

                            {isLogin && (
                                <div className='mt-1 flex justify-end'>
                                    <a href='/forgot-password' className='text-xs text-mainblue-500 hover:text-mainblue-400'>
                                        Forgot password?
                                    </a>
                                </div>
                            )}

                            <button
                                type='submit'
                                className='mt-5 action-button w-full h-[52px] rounded-lg! text-base!'
                                disabled={loading}
                            >
                                {loading
                                    ? isLogin
                                        ? 'Logging in...'
                                        : 'Creating account...'
                                    : isLogin
                                        ? 'Log in'
                                        : 'Get started for free'
                                }
                            </button>
                        </form>

                        <div className='my-5 flex items-center gap-3.5'>
                            <div className='flex-grow h-px bg-[#3a3a3a]'></div>
                            <span className='text-xs text-[#6b6b6b]'>OR</span>
                            <div className='flex-grow h-px bg-[#3a3a3a]'></div>
                        </div>

                        <a
                            href={`/auth/google?returnTo=${encodeURIComponent(returnTo)}`}
                            className='neutral-button2 w-full h-[52px]! justify-center gap-3! '
                        >
                            <svg width='20' height='20' viewBox='0 0 18 18'>
                                <path fill='#4285F4' d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z'/>
                                <path fill='#34A853' d='M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z'/>
                                <path fill='#FBBC05' d='M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z'/>
                                <path fill='#EA4335' d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z'/>
                            </svg>
                            {isLogin ? 'Continue with Google' : 'Sign up with Google'}
                        </a>

                        <div className='mt-4.5 text-center text-[13px] text-[#8a8a8a]'>
                            {isLogin
                                ? "Don't have an account?"
                                : 'Already have an account?'
                            }{' '}
                            <a href={isLogin ? '/signup' : '/login'} className='text-green1 hover:text-green2 font-medium'>
                                {isLogin ? 'Sign up free' : 'Log in'}
                            </a>
                        </div>

                        <div className='mt-6 text-center text-xs11 text-[#6b6b6b]'>
                            By creating an account, you agree to our{' '}
                            <a href='/terms' target='_blank' className='underline text-[#8a8a8a] hover:opacity-80'>Terms of Service</a>{' '}
                            and{' '}
                            <a href='/privacy' target='_blank' className='underline text-[#8a8a8a] hover:opacity-80'>Privacy Policy</a>.
                        </div>

                        <div className='mt-12 flex'>
                            <div className='text-[#6b6b6b] text-xs'>Trusted by 47,000+ creators</div>
                            <div className='flex-1'></div>
                            <div className='w-px bg-[#3a3a3a]'></div>
                            <div className='flex-1'></div>
                            <div className='flex items-center gap-1.5 text-[#6b6b6b] text-xs11'>
                                <div className='text-[#ffb800] text-xs'>★★★★★</div>
                                <div className='text-[#8a8a8a] font-medium'>4.8</div>
                                <div className=''>TrustPilot</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
